/*********************************************************************
 *  OmniDisclose  ▸  Login, Authentication, Routing & Licence Logic  *
 *  ───────────────────────────────────────────────────────────────── *
 *  ▸ Secure MSAL token handling (session-only)                      *
 *  ▸ requireAuth()   – page guard                                   *
 *  ▸ signOut()       – one-click logout                             *
 *  ▸ getUserInfo()   – exposes {name,email,…} for logging/UI        *
 *  ▸ All original licence + Excel-named-range logic retained        *
 *********************************************************************/

/* global Office, Excel */

/** @type {typeof import("@azure/msal-browser")} */
// @ts-ignore – added by CDN, not in DOM typings
const msalLib = window.msal;

import { logAndRethrow } from "../utils.js"; // unchanged
import { runMiniIntegrityCheck } from "../../model/integrity.js";

/* ------------------------------------------------------------------ */
/*  Helpers (UNCHANGED from your original file)                        */
/* ------------------------------------------------------------------ */
/** @param {boolean} isLoading */
function toggleLoading(isLoading) {
  const overlay = document.getElementById("loading-overlay");
  const btn = /** @type {HTMLButtonElement | null} */ (document.getElementById("login-button"));

  overlay?.classList.toggle("d-none", !isLoading);
  overlay?.setAttribute("aria-hidden", String(!isLoading));
  if (btn) btn.disabled = isLoading;
}

/**
 * Retry an async operation with exponential backoff.
 * @template T
 * @param {() => Promise<T>} operation
 * @param {{ retries?: number, delay?: number, factor?: number }} [options]
 * @returns {Promise<T>}
 */
async function withRetry(operation, { retries = 3, delay = 5000, factor = 2 } = {}) {
  /** @type {Window & typeof globalThis & {
   *   serviceDown?: boolean,
   *   showOfflineBanner?: (reason?: boolean | 'service' | 'network') => void
   * }} */
  const w = /** @type {any} */ (window);

  let attempt = 0;
  let current = delay;

  // non-constant for linter
  while (attempt <= retries) {
    if (w.serviceDown) {
      throw new Error("Retry cancelled: service unavailable");
    }
    try {
      return await operation();
    } catch (err) {
      if (attempt === retries || w.serviceDown) {
        if (navigator.onLine) w.showOfflineBanner?.("service");
        throw err;
      }
      await new Promise((r) => setTimeout(r, current));
      current *= factor;
      attempt += 1;
    }
  }
  throw new Error("withRetry: exhausted without success");
}

/* ---------- Offline banner ---------- */
/**
 * Show/hide the offline/service banner.
 * Pass `false` to hide, or 'offline' | 'service' to show.
 * @param {false | 'offline' | 'service'} [state=false]
 * @returns {void}
 */
function showOfflineBanner(state = false) {
  /** @type {Window & typeof globalThis & { serviceDown?: boolean }} */
  const w = /** @type {any} */ (window);

  // overlay
  let overlay = document.getElementById("offline-overlay");
  if (!overlay) {
    overlay = document.createElement("div");
    overlay.id = "offline-overlay";
    Object.assign(overlay.style, {
      position: "fixed",
      inset: "0",
      background: "rgba(0,0,0,0.15)",
      backdropFilter: "blur(3px)",
      WebkitBackdropFilter: "blur(3px)",
      zIndex: "2999",
      opacity: "0",
      transition: "opacity .25s ease",
    });
    document.body.appendChild(overlay);
  }

  // toast
  let toast = document.getElementById("offline-banner");
  if (!toast) {
    toast = document.createElement("div");
    toast.id = "offline-banner";
    Object.assign(toast.style, {
      position: "fixed",
      top: "50%",
      left: "50%",
      transform: "translate(-50%,-50%)",
      width: "80%",
      maxWidth: "480px",
      padding: "1.2rem 1.7rem",
      borderRadius: "1rem",
      fontSize: "1.15rem",
      lineHeight: "1.35",
      fontWeight: "400",
      textAlign: "center",
      boxShadow: "0 1.2rem 2.2rem rgba(0,0,0,.25)",
      backdropFilter: "blur(6px)",
      WebkitBackdropFilter: "blur(6px)",
      zIndex: "3000",
      opacity: "0",
      transition: "opacity .25s ease",
    });
    document.body.appendChild(toast);
  }

  // hide & bail
  if (!state) {
    overlay.style.opacity = "0";
    toast.style.opacity = "0";
    setTimeout(() => {
      overlay.classList.add("d-none");
      toast.classList.add("d-none");
    }, 250);
    w.serviceDown = false;
    return;
  }

  /** @typedef {{ icon: string, cls: string, msg: string }} BannerCfg */
  /** @type {Record<'offline' | 'service', BannerCfg>} */
  const CFG = {
    offline: {
      icon: "fa-solid fa-wifi",
      cls: "bg-danger-subtle text-danger-emphasis",
      msg: "You are currently offline.<br>Please reconnect to continue.",
    },
    service: {
      icon: "fa-solid fa-server",
      cls: "bg-warning-subtle text-warning-emphasis",
      msg: "Service unavailable.<br>Please try again in a moment.",
    },
  };

  /** @type {BannerCfg} */
  const cfg = CFG[state]; // state is narrowed to 'offline' | 'service' here

  // keep a global flag so withRetry can see it
  w.serviceDown = state === "service";

  toast.className = "d-flex flex-column align-items-center justify-content-center " + cfg.cls;
  toast.innerHTML = `
    <i class="${cfg.icon} mb-2" style="font-size:2.6rem;"></i>
    <span class="px-2">${cfg.msg}</span>`;

  overlay.classList.remove("d-none");
  toast.classList.remove("d-none");
  requestAnimationFrame(() => {
    overlay.style.opacity = "1";
    toast.style.opacity = "1";
  });
}

/* ──────────────────────────────────────────────────────────────────
   2.  Guard every fetch   (5-second timeout → "service" banner)
────────────────────────────────────────────────────────────────── */
(function () {
  /** @type {Window & typeof globalThis & { _origFetch?: typeof fetch }} */
  const w = /** @type {any} */ (window);

  const TIMEOUT = 5000; // ms

  /**
   * Wrapped fetch with an abort timeout.
   * @param {RequestInfo | URL} url
   * @param {RequestInit} [opts]
   * @param {number} [ms]
   * @returns {Promise<Response>}
   */
  async function guardedFetch(url, opts = {}, ms = TIMEOUT) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), ms);

    try {
      const res = await (w._origFetch ?? w.fetch)(url, { ...opts, signal: ctrl.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res;
    } finally {
      clearTimeout(timer);
    }
  }

  if (!w._origFetch) {
    w._origFetch = w.fetch; // save original once
    w.fetch = /** @type {any} */ (guardedFetch); // patch (cast to avoid signature fuss)
  }
})();

/* ──────────────────────────────────────────────────────────────────
   3.  ensureOnline()  (unchanged call-sites, new banner call)
────────────────────────────────────────────────────────────────── */
async function ensureOnline() {
  if (navigator.onLine) return true; // quick path

  /* optional extra HEAD probe (kept from your code) */
  try {
    await fetch("https://appsforoffice.microsoft.com/favicon.ico", {
      method: "HEAD",
      mode: "no-cors",
      cache: "no-store",
    });
    return true; // online after all
  } catch (_) {
    /* swallow */
  }

  toggleLoading(false);
  showOfflineBanner("offline");
  return false;
}

/* online/offline listeners */
window.addEventListener("online", () => showOfflineBanner(false));
window.addEventListener("offline", () => {
  toggleLoading(false);
  showOfflineBanner("offline");
});

/* ------------------------------------------------------------------ */
/*  NEW – central auth state & exports                                 */
/* ------------------------------------------------------------------ */

/**
 * @typedef {Object} UserInfo
 * @property {string} name
 * @property {string} email
 * @property {string} accountId
 * @property {string} tenantId
 * @property {number | Date} expiresOn
 */
/** @type {import('@azure/msal-browser').PublicClientApplication | null} */
let msalInstance = null;
/** Currently signed-in user (null if signed out) */
/** @type {UserInfo | null} */
let userInfo = null;

/* 3 public helpers –> import from other files */

/** @returns {UserInfo | null} */
export const getUserInfo = () => userInfo;

/** @returns {boolean} */
export const isSignedIn = () => !!userInfo;

/** @returns {Promise<void>} */
export async function requireAuth() {
  await _ensureAuth();
}

/** @returns {Promise<void>} */
export async function signOut() {
  await _doSignOut();
}

/* ------------------------------------------------------------------ */
/*  Office bootstrap                                                   */
/* ------------------------------------------------------------------ */
Office.onReady(() => {
  // login.html → interactive sign-in page
  if (window.location.pathname.endsWith("/login.html")) {
    _initLoginPage();
    return;
  }

  // any other page → make sure user is authenticated
  requireAuth().catch(console.error);
});

/* ------------------------------------------------------------------ */
/*  MSAL initialisation + silent startup                               */
/* ------------------------------------------------------------------ */
async function _initMsal() {
  if (msalInstance) return; // singleton

  toggleLoading(true);

  const cfg = await _fetchMsalConfig();
  const redirectUri = new URL("/auth-popup.html", window.location.origin).href;
  msalInstance = new msalLib.PublicClientApplication({
    auth: {
      clientId: cfg.clientId,
      authority: cfg.authority,
      redirectUri, // <- dynamic (prod or localhost)
      postLogoutRedirectUri: window.location.origin,
    },
    cache: { cacheLocation: "sessionStorage", storeAuthStateInCookie: true },
    system: { loggerOptions: { loggerCallback: console.debug } },
  });
  await msalInstance.initialize();

  // Attempt silent sign-in immediately
  const accts = msalInstance.getAllAccounts();
  if (accts.length) {
    try {
      const silent = await msalInstance.acquireTokenSilent({
        account: accts[0],
        scopes: ["User.Read"],
      });
      await _processToken(silent); // also fetches licence
    } catch {
      /* ignore – ok if first run */
    }
  }
  toggleLoading(false);
}

/* ------------------------------------------------------------------ */
/*  Login.html  → interactive flow                                     */
/* ------------------------------------------------------------------ */
/** @returns {Promise<void>} */
async function _initLoginPage() {
  await _initMsal();

  const btn = document.getElementById("login-button");
  btn?.addEventListener("click", async () => {
    if (!(await ensureOnline())) return;

    toggleLoading(true);
    try {
      // guard msalInstance (narrow from PublicClientApplication | null)
      const msal = msalInstance;
      if (!msal) {
        console.error("MSAL not initialised");
        return;
      }

      const popup = await msal.loginPopup({ scopes: ["User.Read"] });
      await _processToken(popup); // cache user + license
      window.location.replace(await getRedirectPage());
    } catch (err) {
      console.error("Interactive sign-in failed:", err);
      const msg = document.getElementById("login-message");
      if (msg) msg.textContent = "Sign-in failed. Please try again or contact support.";
    } finally {
      toggleLoading(false);
    }
  });
}

/* ------------------------------------------------------------------ */
/*  Auth-guard for every other page                                    */
/* ------------------------------------------------------------------ */
/** @returns {Promise<void>} */
async function _ensureAuth() {
  if (!(await ensureOnline())) return;
  await _initMsal();

  // Already valid? (normalize Date | number to ms)
  if (userInfo) {
    const exp = typeof userInfo.expiresOn === "number" ? userInfo.expiresOn : userInfo.expiresOn.getTime();
    if (Date.now() < exp) return;
  }

  // Try silent refresh (guard null msalInstance)
  try {
    const msal = msalInstance;
    if (!msal) throw new Error("MSAL not initialised");

    const acct = msal.getActiveAccount() || msal.getAllAccounts()[0];
    if (!acct) throw new Error("No cached account");

    const fresh = await msal.acquireTokenSilent({
      account: acct,
      scopes: ["User.Read"],
    });
    await _processToken(fresh);
    return;
  } catch {
    // fall through to redirect
  }

  // Not authenticated → go to login.html
  window.location.replace("/login.html");
}

/* ------------------------------------------------------------------ */
/*  Sign-out                                                          */
/* ------------------------------------------------------------------ */
async function _doSignOut() {
  if (!msalInstance) return;
  sessionStorage.removeItem("licenseData");
  sessionStorage.removeItem("userInfo");
  userInfo = null;

  await msalInstance
    .logoutPopup({
      account: msalInstance.getActiveAccount(),
      postLogoutRedirectUri: "/login.html",
    })
    .catch(console.warn);
}

/* ------------------------------------------------------------------ */
/*  Token → cache user meta + fetch licence                            */
/* ------------------------------------------------------------------ */
/**
 * Cache license, build user profile, set active account.
 * @param {import('@azure/msal-browser').AuthenticationResult} authResult
 */
async function _processToken(authResult) {
  const acct = authResult.account;

  /** @type {{ tid?: string, preferred_username?: string, name?: string }} */
  const claims = /** @type {any} */ (authResult.idTokenClaims) || {};

  // 1) Licence FIRST — abort sign-in if it fails
  if (!sessionStorage.getItem("licenseData")) {
    try {
      const licence = await getOrCreateLicense(
        claims.tid || "", // Azure tenant GUID
        acct?.username || "", // email
        acct?.name || "" // display name
      );
      sessionStorage.setItem("licenseData", JSON.stringify(licence));
    } catch (err) {
      showOfflineBanner("service"); // amber banner once
      throw err; // bubble → login flow stops
    }
  }

  // 2) Cache user meta *after* licence succeeds
  const expiresMs = authResult.expiresOn instanceof Date ? authResult.expiresOn.getTime() : Date.now() + 55 * 60 * 1000; // safety fallback (~55min)

  /** @type {UserInfo} */
  userInfo = {
    name: acct?.name || claims.name || "",
    email: acct?.username || claims.preferred_username || "",
    accountId: acct?.homeAccountId || "",
    tenantId: claims.tid || "",
    expiresOn: expiresMs,
  };

  sessionStorage.setItem("userInfo", JSON.stringify(userInfo));

  // Guard msalInstance before use
  const msal = msalInstance;
  if (msal) {
    msal.setActiveAccount(acct);
  } else {
    console.warn("MSAL not initialised when setting active account");
  }
}

/* ------------------------------------------------------------------ */
/*  Fetch MSAL config (UNCHANGED, just renamed)                        */
/* ------------------------------------------------------------------ */
async function _fetchMsalConfig() {
  const url = "https://omnidisclose-functionapp.azurewebsites.net/api/GetMsalConfig";
  return withRetry(async () => {
    const r = await fetch(url, { cache: "no-store" });
    if (!r.ok) throw new Error(`Failed MSAL config: ${r.status}`);
    return r.json();
  });
}

/* ------------------------------------------------------------------ */
/*  Unhide authorised sheets & delete module sheets not selected      */
/* ------------------------------------------------------------------ */
async function unhideAuthorisedSheets() {
  const json = localStorage.getItem("omniShowPrefixes");
  const lic = JSON.parse(sessionStorage.getItem("licenseData") || "{}");
  const isAdmin = !!lic.isAdmin;
  if (!json) return;

  /* prefixes we KEEP, e.g. ["C ","F "] from download.js */
  const keepLetters = /** @type {string[]} */ (JSON.parse(json)).map((/** @type {string} */ p) => p.trim()); // ["C","F"]

  /* master list of every module prefix letter */
  const MODULE_LETTERS = ["C", "F", "L", "R", "P", "I", "S"];

  /** @param {string} nm */
  const isModule = (nm) => MODULE_LETTERS.includes(nm.charAt(0)) && nm.charAt(1) === " "; // strict “C ”

  /** @param {string} nm */
  const keepSheet = (nm) => {
    if (nm.startsWith("A ")) return true; // admin sheets never deleted
    return !isModule(nm) || keepLetters.includes(nm.charAt(0));
  };

  await Excel.run(async (ctx) => {
    const sheets = ctx.workbook.worksheets;
    sheets.load("items/name,items/visibility");
    await ctx.sync();

    const toKeep = sheets.items.filter((ws) => keepSheet(ws.name));
    const toDelete = sheets.items.filter((ws) => !keepSheet(ws.name));

    // make sure keepers are visible; drop the rest
    toKeep.forEach((ws) => {
      if (ws.name.startsWith("A ")) {
        ws.visibility = isAdmin ? Excel.SheetVisibility.visible : Excel.SheetVisibility.veryHidden;
      } else {
        ws.visibility = Excel.SheetVisibility.visible;
      }
    });

    toDelete.forEach((ws) => {
      ws.visibility = Excel.SheetVisibility.visible;
    });
    toDelete.forEach((ws) => {
      ws.delete();
    });

    await ctx.sync();
  }).catch((err) => console.error("Excel.run:", err));

  localStorage.removeItem("omniShowPrefixes"); // one-shot
}

/* ------------------------------------------------------------------ */
/*  Router                                                            */
/* ------------------------------------------------------------------ */
export async function getRedirectPage() {
  const host = Office.context.host;

  if (host === Office.HostType.Excel) {
    const hasInputTemplate = await Excel.run(async (ctx) => {
      ctx.workbook.names.load("items/name");
      await ctx.sync();
      return ctx.workbook.names.items.some((n) => n.name === "OmniDiscloseInputTemplate");
    });

    if (hasInputTemplate) return "input.html";

    let ok = false;
    try {
      ok = await runMiniIntegrityCheck();
    } catch (e) {
      console.error("Mini integrity check:", e);
    }

    if (ok) {
      await unhideAuthorisedSheets();
      return "taskpane.html";
    }
    return "download.html";
  }

  if (host === Office.HostType.Word) return "word.html";
  if (host === Office.HostType.PowerPoint) return "powerpoint.html";
  return "login.html";
}

/* ------------------------------------------------------------------ */
/*  UNCHANGED – Excel named-range initialiser                          */
/* ------------------------------------------------------------------ */
/**
 * @param {{
 *   canAccess: boolean;
 *   licenseID: string | number;
 *   licenseName: string;
 *   isAdmin: boolean;
 *   Climate_Active: boolean;
 *   Finance_Active: boolean;
 *   Land_Active: boolean;
 *   Resource_Active: boolean;
 *   Pollution_Active: boolean;
 *   Invasive_Active: boolean;
 *   Social_Active: boolean;
 * }} data
 * @param {string | Date} formattedStartDate
 * @param {string | Date} formattedEndDate
 * @param {string} userDisplayName
 * @returns {Promise<void>}
 */
async function initialiseExcelUserNamedRange(data, formattedStartDate, formattedEndDate, userDisplayName) {
  await Excel.run(async (context) => {
    const workbook = context.workbook;
    const names = workbook.names;
    names.load("items/name");
    await context.sync();

    const addInStatusExists = names.items.some((item) => item.name === "AddIn_Status");
    const overlay = document.getElementById("deploy-template-overlay");
    const deployButton = document.getElementById("deploy-template-button");
    deployButton?.replaceWith(deployButton.cloneNode(true)); // remove old listeners

    if (!addInStatusExists) return;

    overlay?.classList.add("d-none");

    workbook.names.getItem("Contract_Date").getRange().values = [[formattedStartDate]];
    workbook.names.getItem("Expiry_Date").getRange().values = [[formattedEndDate]];
    workbook.names.getItem("Can_Access").getRange().values = [[data.canAccess]];
    workbook.names.getItem("LicenseID").getRange().values = [[data.licenseID]];
    workbook.names.getItem("LicenseName").getRange().values = [[data.licenseName]];
    workbook.names.getItem("User_Name").getRange().values = [[userDisplayName]];
    workbook.names.getItem("isAdmin").getRange().values = [[data.isAdmin]];
    workbook.names.getItem("Climate_Active").getRange().values = [[data.Climate_Active]];
    workbook.names.getItem("Finance_Active").getRange().values = [[data.Finance_Active]];
    workbook.names.getItem("Land_Active").getRange().values = [[data.Land_Active]];
    workbook.names.getItem("Resource_Active").getRange().values = [[data.Resource_Active]];
    workbook.names.getItem("Pollution_Active").getRange().values = [[data.Pollution_Active]];
    workbook.names.getItem("Invasive_Active").getRange().values = [[data.Invasive_Active]];
    workbook.names.getItem("Social_Active").getRange().values = [[data.Social_Active]];
    await context.sync();
  }).catch((e) => logAndRethrow(e, "Error initialising Excel user named range"));
}

/* ------------------------------------------------------------------ */
/*  UNCHANGED – Licence helper                                         */
/* ------------------------------------------------------------------ */
/**
 * Create or fetch a license record for the user.
 * @param {string} tenantId
 * @param {string} userEmail
 * @param {string} userDisplayName
 * @returns {Promise<any>}
 */
async function getOrCreateLicense(tenantId, userEmail, userDisplayName) {
  toggleLoading(true);
  const url = "https://omnidisclose-functionapp.azurewebsites.net/api/GetOrCreateLicense";
  const payload = { tenantId, userEmail, userDisplayName };

  /** @type {Window & typeof globalThis & { _origFetch?: typeof fetch }} */
  const w = /** @type {any} */ (window);

  try {
    const data = await withRetry(
      async () => {
        const r = await (w._origFetch ?? w.fetch)(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
        return r.json();
      },
      { retries: 3, delay: 1500 }
    );

    //console.log("License data:", JSON.stringify(data, null, 2));

    /* -------- format yyyy-mm-dd* → dd mmm yyyy ----------------------- */
    /** @param {string | number | Date | null | undefined} iso */
    const fmt = (iso) =>
      iso
        ? new Date(iso).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short", // Jan, Feb, Mar …
            year: "numeric",
            timeZone: "Australia/Sydney", // ensure AEST/AEDT view
          })
        : "";

    const start = fmt(/** @type {any} */ (data).licenseStartDate); // e.g. 07 Jan 2025
    const end = fmt(/** @type {any} */ (data).licenseEndDate); // e.g. 07 Jan 2028

    if (Office.context.host === Office.HostType.Excel) {
      await initialiseExcelUserNamedRange(/** @type {any} */ (data), start, end, userDisplayName);
    }
    return data;
  } catch (err) {
    console.error("GetOrCreateLicense failed:", err);
  } finally {
    toggleLoading(false);
  }
}

if (typeof window !== "undefined") {
  /** @type {any} */ (window).OmniAuth = {
    requireAuth,
    signOut,
    getUserInfo,
  };
}
